import React from "react";
import CaseStudiesCard from "../CaseStudiesCard";
import Links from "../../../Links";
import Heading from "../../../Heading";
import Stack from "../../../Stack";
import Box from "../../../Box";
import Rule from "../../../Rule";
import BlogLayout from "../BlogLayout";
import Image from "../../../Image";
import ImageOne from "../../../../img/redactd.png";
import ImageTwo from "../../../../img/design-loop.png";
import ImageThree from "../../../../img/workspaces.png";
import ImageFour from "../../../../img/ai.png";
import ImageFive from "../../../../img/search.png";

const Redactd = (
  <CaseStudiesCard id="redactd">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Pair Programming with AI
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Build, compose real web layouts directly with Web Components.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />
    <BlogLayout>
      <Stack spacing="var(--spacing-x-large)">
        <Box>
          <Heading variant="larger" mb="0">
            Redactd
          </Heading>
          <p>
            In mid December I started building Redactd. A web based design canvas built around web components, not React
            or Angular.
          </p>
          <Image dark src={ImageOne} alt="redactd.xyz website" caption="Web Component Canvas for Redactd" />
        </Box>

        <Box>
          <Heading variant="large">Validation</Heading>
          <p>
            Before committing real time, I needed to validate whether the idea was actually worth pursuing. One night
            before bed I opened Lovable, wrote a rough brief, and let it generate a lo fi prototype. I didn’t reuse any
            of the code. I just wanted to see the experience in practice. That was enough to move. I spun up a repo and
            started building it properly.
          </p>
        </Box>

        <Box>
          <Heading variant="large">Build</Heading>
          <p>
            From day one I used my own design system. Not because it was perfect, but because it wasn’t. Building with
            your own system exposes every weakness. Every friction point forces a decision, and both the app and the
            system evolve together.
          </p>
          <p>
            I started with structure, setup, and wiring everything in. Then I moved quickly into the parts that matter.
            For a couple of months I focused on foundations like drag and drop local components, composability, and
            swapping props visually on the canvas. Not flashy work, but it defines everything else.
          </p>
          <p>
            After that, I moved into the features that motivated the build in the first place, prompting the system to
            generate UI and then adjusting the output directly through drag and drop on the canvas.
          </p>
          <Image
            dark
            src={ImageFour}
            alt="redactd.xyz website"
            caption="Prompt → structured canvas output → editable UI system"
          />
          <p>
            In addition to the AI-driven UI generation, I added workflow features like inserting components or recipes
            onto the canvas via search.
          </p>
          <Image
            dark
            src={ImageFive}
            alt="redactd.xyz website"
            caption="Search-driven insertion of components and reusable UI patterns into the canvas."
          />
        </Box>

        <Box>
          <Heading variant="large">AI Collaboration</Heading>
          <p>Most of the early build was done with Claude. We worked through the app in pieces, feature by feature.</p>
          <p>
            It helped a lot, but AI has clear limits. It does not understand craft. It struggles with CSS, consistency,
            and following existing patterns. That part stayed with me.
          </p>
          <p>
            Where it is strong is obvious. Boilerplate, backend setup, and filling gaps in areas outside my expertise.
          </p>
          <p>After about two months, the app already felt far more mature than its age suggested.</p>
          <p>
            Then the Codex Mac app dropped in early February 2026 and the workflow changed again. For the first time I
            could rely on AI to read the codebase, understand the structure, and build features that actually fit the
            system.
          </p>
          <p>
            That only worked because the foundation was already solid. If your patterns are messy, AI amplifies the
            mess. If they are clean, it accelerates everything.
          </p>
          <p>
            This is where combining AI with design system thinking really matters. The system is what makes AI usable at
            scale.
          </p>
        </Box>

        <Box>
          <Heading variant="large">The Pivot</Heading>
          <p>Originally I wanted to support multiple frameworks. That didn’t hold up.</p>
          <p>
            After testing it properly I narrowed it down to one direction, web components only. Not because it is
            popular, it is not, but because it is the correct abstraction layer.
          </p>
          <p>
            Most teams build in frameworks and then, if they want to support web components, they wrap components as web
            components. That is backwards. Build as web components first, then consume them anywhere.
          </p>
          <p>
            It makes the product niche right now, and that is fine. I reworked the architecture so it supports any
            system as long as components are served via CDN or HTTPS, with local components for testing and a default
            library for exploration.
          </p>
          <p>
            Everything runs through JSON and markdown where needed. That decision became important later because it
            allowed AI driven workflows like prompt based generation and synthetic feedback loops.
          </p>
          <Image
            dark
            src={ImageTwo}
            alt="Synthetic feedback loops for rapid iteration"
            caption="Synthetic feedback loops for rapid iteration"
          />
        </Box>

        <Box>
          <Heading variant="large">Problems</Heading>
          <p>
            The canvas was the hardest part. It worked perfectly within my system, but broke as soon as I introduced
            other systems. Font sizing differences, theme naming conflicts, and inconsistent assumptions across
            implementations.
          </p>
          <p>
            This is where I had to lean heavily on past experience, especially typography on the web. Understanding how
            type scales behave, how root sizing cascades, and how different systems interpret spacing and rhythm became
            critical. Without that foundation, I would not have been able to reason through what was breaking or why.
            You can learn more about my thinking on typography on the web in this{" "}
            <Links href="https://youtu.be/FFGSy0EfA9Y" target="_blank" rel="noopener noreferrer">
              video
            </Links>
            .
          </p>
          <p>I had to rebuild the core architecture. Painful, but it made everything more robust.</p>
          <p>
            Undo and redo also caused issues. There was a bug where history kept incrementing incorrectly. I could not
            fix it at the time, so I turned it into a feature, a simple HP style system to track state changes.
          </p>
          <p>Later, with Codex, I fixed it properly, but kept the concept because it added personality.</p>
        </Box>

        <Box>
          <Heading variant="large">Result</Heading>
          <p>
            To demonstrate the workflow in practice, here’s a real example from Redactd. This shows how interface design
            in Redactd can be translated directly into working code through a structured JSON layer.
          </p>
          <p>
            <strong>Example:</strong> Settings panel to control workspace visibility (Private/Shared) in Redactd.
          </p>
          <ol style={{ paddingInlineStart: "var(--spacing-medium)" }}>
            <li>Built the UI with real components in Redactd Canvas and exported it as JSON</li>
            <li>Used Codex to implement the feature directly from the JSON spec</li>
            The canvas was the hardest part. It worked perfectly within my system, but broke as soon as I introduced
            other systems. Font sizing differences, theme naming conflicts, and inconsistent assumptions across
            implementations.
          </ol>
          <Image
            dark
            src={ImageThree}
            alt="Design Canvas → Codex via JSON spec to implement new features."
            caption="Design Canvas → Codex via JSON spec to implement new features."
          />
        </Box>

        <Box>
          <Heading variant="large">Time to Reflect</Heading>
          <p>
            Right now I am stepping back after five months of building 24/7. I let my Codex subscription lapse for now.
            That’s it for this phase. If you want to see more, the build is on{" "}
            <Links
              href="https://www.youtube.com/channel/UCply9WiCRleecD_2RK1Of8A"
              target="_blank"
              rel="noopener noreferrer"
            >
              Youtube
            </Links>
            .
          </p>
          <p>
            Read on Medium:{" "}
            <Links
              href="https://medium.com/@trilfordm/how-i-built-redactd-by-combining-ai-and-design-system-thinking-2a93db225335"
              target="_blank"
              rel="noopener noreferrer"
            >
              How I Built Redactd
            </Links>
          </p>
        </Box>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default Redactd;
